import React from 'react';
import PropTypes from 'prop-types';
import { Select, Tooltip } from 'antd';
import { convertISOToDateFormat } from '../../../../../../util/timezone';
import AlertMetrics from '../AlertMetrics';
import style from './index.scss';

const { Option } = Select;

// Parity with the Classic view. Classic's header for this same page is
// SmartAssessmentTopPanel (index.js:309-322, the `isFromCM` block), which renders
// exactly these three labels from the getAMLSCommonAlertDetails response:
//   Alert ID     -> data.alertId
//   Alert Source -> the literal 'Fraud Prevention'  (NOT data.alertSourceId,
//                   which is null on FRAML-P alerts)
//   Alert Date   -> convertISOToDateFormat(data.alertDate)
// followed by alertMetaSuffix. We read the same slice so the two views can't drift.
const ALERT_SOURCE_LABEL = 'Fraud Prevention';

const EMPTY = '—';

/* FIN-14024 (reopened) — THE ALERT ID IS TRUNCATED FOR DISPLAY ONLY.
   Live ids run to 30 characters ("FS20261004053325675QASECNEG_12"), which
   stretched the first header field and pushed everything after it along.

   FORMAT AS MEGH SPECIFIED, NUMBER MEASURED AGAINST THE LAYOUT. Beyond the cap
   the display shows the head plus TWO DOTS — not an ellipsis character — with
   the full id on the tooltip. That part is his.

   The cap itself is 18, not the 20 first asked for. This field's width decides
   whether the metrics strip fits beside the header fields, and the strip
   dropping to its own line is the misalignment this ticket is actually about.
   Checked on a standard window: 15 through 18 keep it inline, 20 does not. 18
   is therefore the most of the id we can show without reintroducing the wrap —
   raise it and the header breaks again.

   Character count, NOT a CSS width. `EllipsisBlock` (the alert list's approach)
   cuts wherever the box ends, so the same id truncates differently as the
   column resizes; this needs a fixed, predictable cut. */
export const ALERT_ID_MAX = 18;
export const ALERT_ID_HEAD = ALERT_ID_MAX - 2;

export const truncateAlertId = (id) =>
  typeof id === 'string' && id.length > ALERT_ID_MAX
    ? `${id.slice(0, ALERT_ID_HEAD)}..`
    : id;

// Fallback Status vocabulary — used only when the live CM controls
// (alertMetaSuffix) aren't injected (e.g. non-CM route / mock preview).
const STATUS_OPTIONS = [
  { value: 'UNASSIGNED', label: 'Unassigned' },
  { value: 'WORK_IN_PROGRESS', label: 'Work in progress' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'CLOSED', label: 'Closed' }
];

const AlertHeaderCard = ({
  alertDetails,
  header,
  assignees,
  alertMetaSuffix,
  onStatusChange,
  onAssigneeChange
}) => (
  <div className={style.headerCard}>
    <div className={style.headerRow}>
      <div className={style.field}>
        <div className={style.label}>Alert ID</div>
        {/* The tooltip is attached ONLY when something was actually cut — one
            repeating the text already on screen is noise. */}
        {alertDetails?.alertId ? (
          alertDetails.alertId.length > ALERT_ID_MAX ? (
            /* ABOVE, AND LIFTED CLEAR OF THE LABEL. The trigger is the VALUE,
               and its own label sits directly above it, so a top-placed tooltip
               lands on "Alert ID" at antd's default spacing. The extra offset
               pushes it past the label rather than onto it. */
            <Tooltip
              title={alertDetails.alertId}
              placement="topLeft"
              align={{ offset: [0, -12] }}
              overlayClassName={style.idTooltip}>
              <div className={style.value}>
                {truncateAlertId(alertDetails.alertId)}
              </div>
            </Tooltip>
          ) : (
            <div className={style.value}>{alertDetails.alertId}</div>
          )
        ) : (
          <div className={style.value}>{EMPTY}</div>
        )}
      </div>

      <div className={style.field}>
        <div className={style.label}>Alert Source</div>
        <div className={style.value}>{ALERT_SOURCE_LABEL}</div>
      </div>

      <div className={style.field}>
        <div className={style.label}>Alert Date</div>
        {/* Guard on the value BEFORE formatting. convertISOToDateFormat calls
            moment(isoDate), and moment(undefined) is `now` — valid, so it
            formats TODAY'S date. alertDetails is undefined on first render
            (before getAMLSCommonAlertDetails resolves), so without this guard
            the card shows today's date as the alert date until the fetch lands,
            and permanently if the BE ever omits alertDate. moment(null) IS
            invalid, which is why this only bites on undefined. */}
        <div className={style.value}>
          {alertDetails?.alertDate
            ? convertISOToDateFormat(alertDetails.alertDate) || EMPTY
            : EMPTY}
        </div>
      </div>

      {alertMetaSuffix ? (
        // Reuse the live, backend-wired Status + Assign-to (+ Note) controls
        // injected by CM AlertDetails (alertControls) — same as the Classic view.
        <div className={style.controls}>{alertMetaSuffix}</div>
      ) : (
        <>
          <div className={`${style.field} ${style.fieldStatus}`}>
            <label
              className={`${style.label} ${style.labelControl}`}
              htmlFor="spatial-alert-status"
            >
              Status
            </label>
            <Select
              id="spatial-alert-status"
              value={header.status}
              onChange={onStatusChange}
              getPopupContainer={(t) => t.parentNode}
            >
              {STATUS_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
          </div>

          <div className={`${style.field} ${style.fieldAssignee}`}>
            <label
              className={`${style.label} ${style.labelControl}`}
              htmlFor="spatial-alert-assignee"
            >
              Assign to
            </label>
            <Select
              id="spatial-alert-assignee"
              placeholder="Please select..."
              value={header.assignedTo || undefined}
              onChange={onAssigneeChange}
              getPopupContainer={(t) => t.parentNode}
              allowClear
            >
              {(assignees || []).map((a) => (
                <Option key={a.value} value={a.value}>
                  {a.label}
                </Option>
              ))}
            </Select>
          </div>
        </>
      )}

      {/* Variants Breached / Action / Alert Score — the same counters Classic
          shows, in the Spatial design's single bordered strip. LAST in the row and
          pushed to the far right by its own `margin-left: auto`, matching the
          design; the identity fields and controls keep the left. */}
      <AlertMetrics alertDetails={alertDetails} />
    </div>
  </div>
);

AlertHeaderCard.propTypes = {
  // Live getAMLSCommonAlertDetails response (state.amlsCommon.alertDetails.data).
  alertDetails: PropTypes.object,
  // Local UI state, used only by the fallback Status/Assign stubs below.
  header: PropTypes.object.isRequired,
  assignees: PropTypes.array,
  // Live CM Status/Assign/Note controls (alertControls); falls back to stubs.
  alertMetaSuffix: PropTypes.node,
  onStatusChange: PropTypes.func,
  onAssigneeChange: PropTypes.func
};

export default AlertHeaderCard;
